import React, { useEffect, useMemo, useState } from 'react'
import ReactCountryFlag from 'react-country-flag'
import type { EntityId } from '../../types/constants'
import { LoadingComponent } from '../../components/Loading'
import {
    SettingItem,
    SettingsTextInput,
    SettingsText,
    SettingsCard,
} from '../../components/settings/SettingsCard'
import { axiosPatch } from '../../util/api'
import { clearAndFetchUser } from '../../actions/user'
import { Toasts } from '../../components/toaster/Toast'
import { useDispatch } from 'react-redux'

interface AdAccountInformationProps {
    selectedProfile: Models.Profile
}

export const AdAccountInformation = ({ selectedProfile }: AdAccountInformationProps) => {
    const dispatch = useDispatch()
    const [loading, setLoading] = useState(false)
    const [nickname, setNickname] = useState<string>('')
    const [name, setName] = useState<string>('')
    const [entityId, setEntityId] = useState<string>('')
    const [profileId, setProfileId] = useState<EntityId>(0)

    const MarketplaceComponent = () => {
        const countryCode =
            selectedProfile.country_code === 'UK' ? 'gb' : selectedProfile.country_code
        return (
            <div className="flex items-center">
                {countryCode ? (
                    <ReactCountryFlag countryCode={countryCode} svg className="mr-1 text-lg" />
                ) : (
                    <LoadingComponent size={18} />
                )}
                <p className="uppercase">{countryCode}</p>
            </div>
        )
    }

    useEffect(() => {
        setNickname(selectedProfile.nickname ?? '')
        setName(selectedProfile.name)
        setEntityId(selectedProfile.entity_id)
        setProfileId(selectedProfile.profile_id)
    }, [selectedProfile])

    const dirty = useMemo(() => {
        return nickname !== (selectedProfile.nickname ?? '')
    }, [nickname, selectedProfile.nickname])

    const amazonAdAccountDescription = (
        <span>
            The name set in your Amazon Ad Console settings. Your can update the name
            <a
                target="_blank"
                className="text-mjBlue-500"
                href="https://advertising.amazon.com/accounts"
                rel="noreferrer"
            >
                &nbsp;here
            </a>
            .
        </span>
    )

    const handleSave = () => {
        setLoading(true)
        axiosPatch(`profiles/${selectedProfile.profile_id}`, {
            ...selectedProfile,
            nickname,
        })
            .then((_) => {
                dispatch(clearAndFetchUser())
                setLoading(false)
                Toasts.success('Nickname saved')
                setNickname('')
            })
            .catch(() => {
                setLoading(false)
            })
    }

    return (
        <SettingsCard
            open
            title="Ad Account Information"
            onSave={dirty ? handleSave : null}
            loading={loading}
        >
            <SettingItem
                title="Marketplace"
                description="The country or region associated with the ad account."
                rightComponent={<MarketplaceComponent />}
            />
            <SettingItem
                title="Merch Jar Nickname"
                description="The name displayed by the Merch Jar app for the ad account. Leave blank to use the Amazon Ad Account Name."
                rightComponent={
                    <SettingsTextInput loading={loading} value={nickname} onChange={setNickname} />
                }
            />
            <SettingItem
                title="Amazon Ad Account Name"
                description={amazonAdAccountDescription}
                rightComponent={<SettingsText text={name} />}
            />
            <SettingItem
                title="Entity ID"
                description="The unique ad account ID assigned by Amazon."
                rightComponent={<SettingsText text={entityId} />}
            />
            <SettingItem
                bottomDivider={false}
                title="API Numeric ID"
                description="The unique ID used by the Amazon Advertising API."
                rightComponent={<SettingsText text={profileId} />}
            />
        </SettingsCard>
    )
}
